import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { Archive, SwapHoriz, VisibilityOutlined } from "@mui/icons-material";
import {
  useLazyGetBranchWiseUnloadingStatsQuery,
  useLazyGetBranchShipmentsListQuery,

} from "store/services/driverApi";
import ShipmentsListModal from "components/Shared/ShipmentsListModal";
import { useLazyGetTransitShipmentCountQuery } from "store/services/shipmentLoadingApi";

function UnloadingBranchPanel({ driverId }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);

  const [
    getBranchWiseUnloadingStats,
    {
      data: unloadingAggregation,
      isLoading: isUnloadingLoading,
      error: unloadingError,
    },
  ] = useLazyGetBranchWiseUnloadingStatsQuery();

  const [
    getBranchShipmentsList,
    {
      data: shipmentsData,
      isLoading: isShipmentsLoading,
      error: shipmentsError,
    },
  ] = useLazyGetBranchShipmentsListQuery();

  // new
  const [
    getTransitShipmentCount,
    {
      data: transitCountData,
      isLoading: isTransitCountLoading,
    },
  ] = useLazyGetTransitShipmentCountQuery();

  useEffect(() => {
    if (driverId) {
      getBranchWiseUnloadingStats({ driverId });
      getTransitShipmentCount({ driverId }); // new
    }
  }, [driverId, getBranchWiseUnloadingStats, getTransitShipmentCount]);

  const handleShipmentCountClick = (branch) => {
    setSelectedBranch(branch);
    setIsModalOpen(true);
    getBranchShipmentsList({
      driverId,
      branchId: branch._id,
      type: "unloading",
    });
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedBranch(null);
  };

  const branchData = unloadingAggregation?.data || [];
  const totalToUnloadShipments = branchData.reduce(
    (acc, branch) => acc + (branch.unloadingCount || 0),
    0
  );

  const transitShipmentCount = transitCountData?.data?.totalTransitShipments ?? 0; // new

  return (
    <Grid container spacing={2} mt={2}>
      {/* Table: To Unload Shipments */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 4, boxShadow: 2, height: "100%" }}>
          <Box display="flex" alignItems="center" mb={2}>
            <Archive sx={{ color: "secondary.main", fontSize: 28, mr: 1 }} />
            <Typography variant="h6" fontWeight="bold" className="text-gray-900 dark:text-white">
              To Unload Shipments{" "}
              <Typography component="span" variant="h6" fontWeight="bold" sx={{ color: "primary.main" }}>
                ({totalToUnloadShipments})
              </Typography>
            </Typography>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" }, fontWeight: "bold" }}>
                    Branch Name
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" }, fontWeight: "bold", textAlign: "center" }}>
                    Shipments
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" }, fontWeight: "bold" }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {isUnloadingLoading ? (
                  <TableRow key="loading">
                    <TableCell colSpan={3} align="center">
                      <Box display="flex" justifyContent="center" alignItems="center" py={2}>
                        <CircularProgress size={24} sx={{ mr: 1 }} />
                        Loading unloading data...
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : unloadingError ? (
                  <TableRow key="error">
                    <TableCell colSpan={3} align="center">
                      <Typography color="error">
                        Error loading unloading data:{" "}
                        {unloadingError?.data?.message || unloadingError?.message || "Unknown error"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : branchData.length > 0 ? (
                  branchData.map((branch, index) => (
                    <TableRow
                      key={branch._id || index}
                      sx={{ "&:hover": { bgcolor: (theme) => theme.palette.action.hover } }}
                    >
                      <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                        {branch.branchName}
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" }, fontWeight: "bold", textAlign: "center" }}>
                        {branch.unloadingCount}
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                        <IconButton
                          onClick={() => handleShipmentCountClick(branch)}
                          size="small"
                          color="primary"
                        >
                          <VisibilityOutlined />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow key="no-data">
                    <TableCell colSpan={3} align="center">
                      No unloading data available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>

      {/* Transit Shipment Count Box — new */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 4, boxShadow: 2, height: "100%" }}>
          <Box display="flex" alignItems="center" mb={2}>
            <SwapHoriz sx={{ color: "secondary.main", fontSize: 28, mr: 1 }} />
            <Typography variant="h6" fontWeight="bold" className="text-gray-900 dark:text-white">
               To unLoad Transit Shipments
            </Typography>
          </Box>
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: "200px" }}>
            {isTransitCountLoading ? (
              <Box display="flex" flexDirection="column" alignItems="center">
                <CircularProgress size={32} sx={{ mb: 1 }} />
                <Typography variant="body2" color="textSecondary">
                  Loading count...
                </Typography>
              </Box>
            ) : (
              <Box textAlign="center">
                <Typography
                  variant="h3"
                  fontWeight="bold"
                  color="primary.main"
                  sx={{ fontSize: { xs: "2.5rem", sm: "3rem" } }}
                >
                  {transitShipmentCount}
                </Typography>
                <Typography variant="body1" color="textSecondary" mt={1}>
                  Shipments In Transit
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Grid>

      <ShipmentsListModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        shipments={shipmentsData?.data || []}
        isLoading={isShipmentsLoading}
        error={shipmentsError?.message || null}
        branchName={selectedBranch?.branchName || ""}
        branchType="unloading"
      />
    </Grid>
  );
}

export default UnloadingBranchPanel;